import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, X, Clock, CheckCircle, XCircle } from "lucide-react";
import { API } from "./_apiConfig";

const ApplicationsManagement = () => {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await fetch(`${API}/admin/team-applications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setApplications(data);
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (appId) => {
    if (!window.confirm("Approuver cette candidature ? Un compte éditeur sera créé.")) return;

    try {
      const response = await fetch(`${API}/admin/team-applications/${appId}/approve`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        toast.info(`Email: ${data.credentials.email}\nMot de passe: ${data.credentials.default_password}`, {
          duration: 10000,
        });
        fetchApplications();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur");
      }
    } catch (error) {
      console.error("Error approving:", error);
      toast.error("Erreur lors de l'approbation");
    }
  };

  const handleReject = async (appId) => {
    if (!window.confirm("Refuser cette candidature ?")) return;

    try {
      const response = await fetch(`${API}/admin/team-applications/${appId}/reject`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Candidature refusée");
        fetchApplications();
      }
    } catch (error) {
      console.error("Error rejecting:", error);
      toast.error("Erreur");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="text-yellow-500 border-yellow-500"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
      case "approved":
        return <Badge variant="outline" className="text-green-500 border-green-500"><CheckCircle className="h-3 w-3 mr-1" />Approuvée</Badge>;
      case "rejected":
        return <Badge variant="outline" className="text-red-500 border-red-500"><XCircle className="h-3 w-3 mr-1" />Refusée</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const pendingCount = applications.filter(a => a.status === "pending").length;

  return (
    <div data-testid="admin-applications">
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground">
          Candidatures Équipe
        </h2>
        {pendingCount > 0 && (
          <Badge className="bg-yellow-500">{pendingCount} en attente</Badge>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : applications.length > 0 ? (
        <div className="space-y-4">
          {applications.map((app) => (
            <Card key={app.id} className={app.status === "pending" ? "border-yellow-500/30" : ""}>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-foreground">{app.full_name}</h3>
                      {getStatusBadge(app.status)}
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">{app.email}</p>
                    <p className="text-sm text-muted-foreground mb-1">
                      <span className="font-medium">Diplôme:</span> {app.last_degree}
                    </p>
                    <p className="text-sm text-muted-foreground mb-2">
                      <span className="font-medium">Domaine:</span> {app.field_of_study}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {app.skills?.map((skill) => (
                        <Badge key={skill} variant="secondary" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Soumise le {new Date(app.created_at).toLocaleDateString("fr-FR")}
                    </p>
                  </div>
                  {app.status === "pending" && (
                    <div className="flex sm:flex-col gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleApprove(app.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approuver
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(app.id)}
                        className="text-red-500 border-red-500 hover:bg-red-500/10"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Refuser
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Aucune candidature pour le moment.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ApplicationsManagement;
